import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Crown,
  Ticket,
  Wallet,
  BarChart3,
  Camera,
  UtensilsCrossed,
  Dumbbell,
  Sparkles,
  CheckCircle2,
} from "lucide-react";
import "./EmbajadoresLanding.css";

export default function EmbajadoresLanding() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [faqOpen, setFaqOpen] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    instagram: "",
    tiktok: "",
    seguidores: "",
    motivo: "",
  });

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Dynamic API URL
    const API_URL = window.location.hostname === 'localhost'
      ? 'http://127.0.0.1:8000/api'
      : 'https://api.wayoai.app/api';

    try {
      const response = await fetch(`${API_URL}/embajadores`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormSubmitted(true);
        setFormData({ nombre: "", email: "", instagram: "", tiktok: "", seguidores: "", motivo: "" });
      }
    } catch (error) {
      console.error("Error enviando solicitud:", error);
    }
  };

  const toggleFaq = (index) => {
    setFaqOpen(faqOpen === index ? null : index);
  };

  // Reveal al hacer scroll. Añadimos la clase por JS (no en el JSX) para que,
  // si el JS falla, el contenido se vea igual. El hero ya tiene su animación.
  useEffect(() => {
    const selectors = [
      ".emb-benefits h2", ".emb-benefit-card",
      ".emb-how h2", ".emb-step",
      ".emb-what-card",
      ".emb-who h2", ".emb-who-tag",
      ".emb-faq h2", ".emb-faq-item",
      ".emb-final-cta h2",
    ];
    const els = Array.from(document.querySelectorAll(selectors.join(",")));
    if (!els.length) return;

    els.forEach((el) => el.classList.add("emb-reveal"));
    els.forEach((el) => {
      const group = Array.from(el.parentElement ? el.parentElement.children : []).filter(
        (c) => c.classList.contains("emb-reveal")
      );
      const i = group.indexOf(el);
      if (i > 0) el.style.transitionDelay = `${Math.min(i * 0.07, 0.35)}s`;
    });

    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -50px 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const faqItems = [
    {
      q: "¿Tiene algún coste unirse?",
      a: "No. El programa es completamente gratuito. Tú recibes Premium gratis y ganas comisión por cada suscripción."
    },
    {
      q: "¿Necesito un mínimo de seguidores?",
      a: "No pedimos un mínimo. Lo que valoramos es que tu contenido esté relacionado con fitness, nutrición, salud o estilo de vida saludable."
    },
    {
      q: "¿Cuánto puedo ganar?",
      a: "No hay límites. Cuanto más promociones tu código y más suscripciones consigas, más ganas."
    },
    {
      q: "¿Qué tengo que publicar?",
      a: "No hay obligación de publicar un número mínimo de veces. Lo que funciona mejor es mostrar la app en uso: escanear tu comida en stories y mostrar el resultado. Es natural y visual. Sin guiones, sin forzar."
    },
    {
      q: "¿Qué reciben mis seguidores?",
      a: "Con tu código, tus seguidores reciben 7 días de Premium gratis. Escaneos ilimitados, recetas, entrenos y recomendaciones personalizadas. Después pueden decidir si continúan."
    },

  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Helmet>
        <title>Programa de Embajadores | Wayo - Gana Dinero Compartiendo Nutrición</title>
        <meta
          name="description"
          content="Únete al programa de embajadores de Wayo. Premium gratis, código personalizado y 10€ por cada suscripción. Para creadores de contenido fitness, nutrición y salud. Sin mínimo de seguidores."
        />
        <meta
          name="keywords"
          content="programa de embajadores fitness, programa de afiliados nutrición, ganar dinero como creador de contenido fitness, embajador de marca fitness, código de descuento influencer, monetizar contenido de salud"
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://wayoai.app/embajadores" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Wayo" />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:title" content="Programa de Embajadores | Wayo" />
        <meta
          property="og:description"
          content="Premium gratis, tu código personalizado y 10€ por cada suscripción. Sin mínimo de seguidores."
        />
        <meta property="og:url" content="https://wayoai.app/embajadores" />
        <meta property="og:image" content="https://wayoai.app/assets/img/og-wayo.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Programa de Embajadores | Wayo" />
        <meta
          name="twitter:description"
          content="Premium gratis, código personalizado y 10€ por cada suscripción."
        />

        {/* FAQ structured data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="emb-page">
        {/* NAV */}
        <nav className="emb-nav">
          <div className="emb-container">
            <Link to="/" className="emb-nav-logo-link">
              <img
                src="/assets/img/wayo-logo.png"
                alt="Wayo"
                className="emb-nav-logo"
              />
            </Link>
            <a href="#formulario" className="emb-nav-cta">Quiero ser embajador</a>
          </div>
        </nav>

        {/* HERO */}
        <section className="emb-hero">
          <div className="emb-container">
            <div className="emb-hero-badge">PROGRAMA DE EMBAJADORES 2026</div>
            <h1>
              Recomienda lo que ya usas.<br />
              <span className="emb-gradient">Gana por cada suscripción.</span>
            </h1>
            <p>Únete al programa de embajadores de Wayo: Premium gratis, tu código personalizado y 10€ por cada suscripción que consigas.</p>
            <a href="#formulario" className="emb-hero-cta">
              Unirme al programa →
            </a>
            <div className="emb-hero-stats">
              <div>
                <div className="emb-hero-stat-number">1.700+</div>
                <div className="emb-hero-stat-label">Usuarios activos</div>
              </div>
              <div>
                <div className="emb-hero-stat-number">10€</div>
                <div className="emb-hero-stat-label">Por cada suscripción</div>
              </div>
              <div>
                <div className="emb-hero-stat-number">0€</div>
                <div className="emb-hero-stat-label">Coste para ti</div>
              </div>
            </div>
          </div>
        </section>

        {/* BENEFITS */}
        <section className="emb-benefits">
          <div className="emb-container">
            <h2>Lo que recibes como embajador</h2>
            <div className="emb-benefits-grid">
              <div className="emb-benefit-card">
                <div className="emb-benefit-icon"><Crown size={30} color="#0ea5e9" strokeWidth={1.75} /></div>
                <div className="emb-benefit-title">Premium gratis</div>
                <div className="emb-benefit-desc">Acceso completo a todas las funciones de Wayo: escaneos ilimitados, recetas, entrenos y planes personalizados.</div>
              </div>
              <div className="emb-benefit-card">
                <div className="emb-benefit-icon"><Ticket size={30} color="#0ea5e9" strokeWidth={1.75} /></div>
                <div className="emb-benefit-title">Tu código personalizado</div>
                <div className="emb-benefit-desc">Un código único con tu nombre. Tus seguidores lo usan al registrarse y reciben 7 días gratis de Premium.</div>
              </div>
              <div className="emb-benefit-card">
                <div className="emb-benefit-icon"><Wallet size={30} color="#0ea5e9" strokeWidth={1.75} /></div>
                <div className="emb-benefit-title">10€ por suscripción</div>
                <div className="emb-benefit-desc">Por cada persona que se suscriba al plan anual con tu código, ganas 10€ de comisión. Sin límite.</div>
              </div>
              <div className="emb-benefit-card">
                <div className="emb-benefit-icon"><BarChart3 size={30} color="#0ea5e9" strokeWidth={1.75} /></div>
                <div className="emb-benefit-title">Panel de estadísticas</div>
                <div className="emb-benefit-desc">Consulta en tiempo real cuántas personas usan tu código, cuántas se suscribieron y cuánto has generado.</div>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="emb-how">
          <div className="emb-container">
            <h2>Cómo funciona</h2>
            <div className="emb-steps">
              <div className="emb-step">
                <div className="emb-step-number">1</div>
                <div>
                  <div className="emb-step-title">Regístrate aquí</div>
                  <div className="emb-step-desc">Rellena el formulario con tu nombre e Instagram. Revisamos tu perfil y te activamos en menos de 24h.</div>
                </div>
              </div>
              <div className="emb-step">
                <div className="emb-step-number">2</div>
                <div>
                  <div className="emb-step-title">Recibe tu código y Premium</div>
                  <div className="emb-step-desc">Te activamos Premium gratis y te enviamos tu código personalizado (ej: tu nombre).</div>
                </div>
              </div>
              <div className="emb-step">
                <div className="emb-step-number">3</div>
                <div>
                  <div className="emb-step-title">Comparte con tu comunidad</div>
                  <div className="emb-step-desc">Muestra Wayo en tus stories o reels. Escanea tu comida, muestra el resultado. Es contenido visual que funciona solo.</div>
                </div>
              </div>
              <div className="emb-step">
                <div className="emb-step-number">4</div>
                <div>
                  <div className="emb-step-title">Cobra por cada suscripción</div>
                  <div className="emb-step-desc">Cada vez que alguien se suscribe con tu código, recibes dinero de comisión. Sin límite. Pagos por transferencia o PayPal.</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IS WAYO */}
        <section className="emb-what">
          <div className="emb-container">
            <div className="emb-what-card">
              <h2>¿Qué es Wayo?</h2>
              <p>Una app de nutrición con IA. Haces una foto a tu comida y en 3 segundos tienes calorías y macros. Sin buscar ingredientes, sin pesar nada.</p>
              <div className="emb-what-features">
                <div className="emb-what-feature">
                  <div className="emb-what-feature-icon"><Camera size={22} color="#0ea5e9" strokeWidth={1.75} /></div>
                  <div className="emb-what-feature-text"><strong>Escaneo con foto</strong>La IA reconoce tu comida al instante</div>
                </div>
                <div className="emb-what-feature">
                  <div className="emb-what-feature-icon"><UtensilsCrossed size={22} color="#0ea5e9" strokeWidth={1.75} /></div>
                  <div className="emb-what-feature-text"><strong>+100 recetas</strong>Con macros calculados, actualizadas cada mes</div>
                </div>
                <div className="emb-what-feature">
                  <div className="emb-what-feature-icon"><Dumbbell size={22} color="#0ea5e9" strokeWidth={1.75} /></div>
                  <div className="emb-what-feature-text"><strong>Planes de entreno</strong>Rutinas para todos los niveles incluidas</div>
                </div>
                <div className="emb-what-feature">
                  <div className="emb-what-feature-icon"><Sparkles size={22} color="#0ea5e9" strokeWidth={1.75} /></div>
                  <div className="emb-what-feature-text"><strong>Recomendaciones IA</strong>Consejos personalizados según tus hábitos</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHO IS IT FOR */}
        <section className="emb-who">
          <div className="emb-container">
            <h2>¿Para quién es el programa?</h2>
            <p>Creadores de contenido que hablen de salud, fitness o estilo de vida. No pedimos mínimo de seguidores.</p>
            <div className="emb-who-tags">
              <div className="emb-who-tag">Entrenadores personales</div>
              <div className="emb-who-tag">Nutricionistas</div>
              <div className="emb-who-tag">Creadores fitness</div>
              <div className="emb-who-tag">Lifestyle saludable</div>
              <div className="emb-who-tag">Coaches de transformación</div>
              <div className="emb-who-tag">Preparadores físicos</div>
              <div className="emb-who-tag">Divulgadores de salud</div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <section className="emb-form" id="formulario">
          <div className="emb-container">
            <div className="emb-form-card">
              {!formSubmitted ? (
                <>
                  <h2>Únete al programa</h2>
                  <p>Revisamos tu perfil y te activamos en menos de 24h</p>
                  <form onSubmit={handleSubmit}>
                    <div className="emb-form-group">
                      <label>Nombre</label>
                      <input
                        type="text"
                        name="nombre"
                        placeholder="Tu nombre"
                        value={formData.nombre}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="emb-form-group">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="emb-form-group">
                      <label>Instagram</label>
                      <input
                        type="text"
                        name="instagram"
                        placeholder="@tuusuario"
                        value={formData.instagram}
                        onChange={handleFormChange}
                        required
                      />
                    </div>
                    <div className="emb-form-group">
                      <label>TikTok (opcional)</label>
                      <input
                        type="text"
                        name="tiktok"
                        placeholder="@tuusuario"
                        value={formData.tiktok}
                        onChange={handleFormChange}
                      />
                    </div>
                    <div className="emb-form-group">
                      <label>Seguidores aproximados</label>
                      <select
                        name="seguidores"
                        value={formData.seguidores}
                        onChange={handleFormChange}
                        required
                      >
                        <option value="">Selecciona</option>
                        <option value="0-1000">Menos de 1.000</option>
                        <option value="1000-5000">1.000 - 5.000</option>
                        <option value="5000-10000">5.000 - 10.000</option>
                        <option value="10000-50000">10.000 - 50.000</option>
                        <option value="50000+">Más de 50.000</option>
                      </select>
                    </div>
                    <div className="emb-form-group">
                      <label>¿Por qué quieres ser embajador de Wayo? (opcional)</label>
                      <textarea
                        name="motivo"
                        rows="3"
                        placeholder="Cuéntanos brevemente..."
                        value={formData.motivo}
                        onChange={handleFormChange}
                      />
                    </div>
                    <button type="submit" className="emb-form-submit">Enviar solicitud</button>
                  </form>
                  <p className="emb-form-note">Sin compromiso. Sin coste. Te contestamos en menos de 24h.</p>
                </>
              ) : (
                <div className="emb-form-success">
                  <div className="emb-success-icon"><CheckCircle2 size={48} color="#10b981" strokeWidth={1.75} /></div>
                  <h3>Tu solicitud se ha registrado con éxito</h3>
                  <p>Está pendiente de validación. ¡En breve nos pondremos en contacto contigo!</p>
                  <div style={{ marginTop: "32px", display: "flex", flexDirection: "column", gap: "16px" }}>
                    <p style={{ fontSize: "14px", color: "var(--emb-gray)", marginBottom: "8px" }}>
                      Mientras esperas, descárgate la app y crea tu usuario para explorar todas las características:
                    </p>

                    {/* App Store */}
                    <a
                      href="https://apps.apple.com/app/id6756500478"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        padding: "16px 20px",
                        background: "rgba(14, 165, 233, 0.1)",
                        border: "1px solid rgba(14, 165, 233, 0.2)",
                        borderRadius: "12px",
                        textDecoration: "none",
                        transition: "all 0.2s",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(14, 165, 233, 0.15)";
                        e.currentTarget.style.borderColor = "rgba(14, 165, 233, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(14, 165, 233, 0.1)";
                        e.currentTarget.style.borderColor = "rgba(14, 165, 233, 0.2)";
                      }}
                    >
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" color="var(--emb-white)">
                        <path d="M18.71 19.86c-.81.89-1.72 1.7-2.75 2.33.73-1.42 1.29-2.92 1.69-4.47h-4.23v-5.5h7.06c.07.37.11.76.11 1.15 0 2.35-.74 4.53-2.02 6.32l.01.17c.65 0 1.3-.09 1.95-.27.19-.06.38-.12.57-.19l-2.38-2.47zm-8.98 8.14c-2.19 0-4.2-.76-5.77-2.02l2.56-2.55c1 .71 2.25 1.13 3.61 1.13 1.4 0 2.62-.42 3.62-1.12l2.56 2.55c-1.58 1.26-3.59 2.02-5.78 2.02zm0-16c-1.36 0-2.61.42-3.61 1.12l-2.56-2.55c1.57-1.26 3.58-2.02 5.77-2.02 2.19 0 4.2.76 5.77 2.02l-2.56 2.55c-1.01-.7-2.22-1.12-3.61-1.12zm-7.5 8.5h7.07v5.5h-4.24c.4 1.55.96 3.05 1.68 4.47-1.03-.63-1.94-1.44-2.75-2.33l-2.56 2.55c-1.26-1.57-2.01-3.58-2.01-5.77 0-2.19.75-4.2 2.01-5.78l2.56 2.55c-.05.3-.13.59-.13.89 0 1.41.43 2.63 1.14 3.62l2.74-2.7zm8.98-8.5c-2.19 0-4.2.76-5.77 2.02l2.56 2.55c1-.71 2.25-1.13 3.61-1.13 1.4 0 2.62.42 3.62 1.12l2.56-2.55c-1.58-1.26-3.59-2.02-5.78-2.02z"/>
                      </svg>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: "12px", color: "var(--emb-gray)" }}>Descargar en</div>
                        <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--emb-white)" }}>App Store</div>
                      </div>
                    </a>

                    {/* Google Play */}
                    <a
                      href="https://play.google.com/store/apps/details?id=com.facundosolis.wayo"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        padding: "16px 20px",
                        background: "rgba(14, 165, 233, 0.1)",
                        border: "1px solid rgba(14, 165, 233, 0.2)",
                        borderRadius: "12px",
                        textDecoration: "none",
                        transition: "all 0.2s",
                        cursor: "pointer"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "rgba(14, 165, 233, 0.15)";
                        e.currentTarget.style.borderColor = "rgba(14, 165, 233, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(14, 165, 233, 0.1)";
                        e.currentTarget.style.borderColor = "rgba(14, 165, 233, 0.2)";
                      }}
                    >
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor" color="var(--emb-white)">
                        <path d="M3 13.5h8v8.5H3z M11 13.5h8v8.5h-8z M3 2h8v8.5H3z M13.5 2H22v8.5h-8.5z"/>
                      </svg>
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: "12px", color: "var(--emb-gray)" }}>Descargar en</div>
                        <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--emb-white)" }}>Google Play</div>
                      </div>
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="emb-faq">
          <div className="emb-container">
            <h2>Preguntas frecuentes</h2>
            <div className="emb-faq-list">
              {faqItems.map((item, idx) => (
                <div
                  key={idx}
                  className={`emb-faq-item ${faqOpen === idx ? 'open' : ''}`}
                >
                  <div
                    className="emb-faq-q"
                    onClick={() => toggleFaq(idx)}
                  >
                    {item.q}
                  </div>
                  <div className="emb-faq-a">{item.a}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="emb-final-cta">
          <div className="emb-container">
            <h2>¿Listo para empezar?</h2>
            <p>Únete al programa, recibe Premium y empieza a ganar con tu contenido.</p>
            <a href="#formulario" className="emb-hero-cta">Quiero ser embajador →</a>
          </div>
        </section>
      </div>
    </>
  );
}
